"use client";
import {
  CheckboxComponent,
  LoaderComponent,
  PaperComponent,
  SelectComponent,
  SnackbarComponent,
  TextFieldComponent,
} from "@/components";

import {
  COMPANY_NAME_REGEX,
  ConsentType,
  FREE_TEXT_REGEX,
  GET_PRODUCT_APPLICATION,
  NAME_REGEX,
  PERSONAL_DETAILS_SCHEMA,
  PRODUCT_HANDLER,
  STAGE,
  STAGE_BASED_ROUTING,
  WEB_COLORS,
  WEB_EVENTS,
} from "@/lib/constants";

import AutoCompleteSearch from "@/components/AutoCompleteSearch";
import { useRouter } from "@/i18n/routing";
import { useFooter } from "@/lib/context/FooterContext";
import useErrorHandling from "@/lib/hooks/errorhandling";
import { sendEventAnalytics } from "@/lib/hooks/eventAnalytics";
import useStageErrorHandler from "@/lib/hooks/stageErrorHandler";
import {
  useAddCustomerInfoMutation,
  useConsentCreateMutation,
  useGetEmployementTypeListQuery,
  useGetGendersListQuery,
  useGetIndustryTypeListQuery,
} from "@/lib/redux";
import moengage from "@moengage/web-sdk";
import { FormHelperText, Grid, Stack, Typography } from "@mui/material";
import { useFormik, FormikProvider } from "formik";
import { useTranslations } from "next-intl";
import { useEffect, useMemo, useState } from "react";
import { AddressStoreMethods } from "@/components/Address";
import FocusErrorField from "@/components/formik/FocusErrorField";
import { logger } from "@/lib/constants/Logger";

const MAX_LIMIT_FOR_NAME = 60;

const logWithAppId = (message, appId, extra = {}, level = "error") => {
  logger?.[level]?.(message, {
    appId,
    component: "PersonalDetailsFields",
    ...extra,
  });
};

function PersonalDetailsFields({
  product,
  applicationData,
  refetchApplicationData,
  customerData,
  setShowPanEditModal,
}) {
  const router = useRouter();
  const t = useTranslations("PersonalDetails.fields");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const { setActions } = useFooter();
  const [cibilConsent, setCibilConsent] = useState(false);
  //API HOOKS
  /** --------------------------RTK-------------------------- **/
  const [
    addInfo,
    {
      isLoading: addInfoLoading,
      error: errorAddInfo,
      isSuccess: updatedCustomerInfo,
    },
  ] = useAddCustomerInfoMutation();

  const [
    createConsent,
    { isLoading: consentDataLoading, error: consentError },
  ] = useConsentCreateMutation();

  const { data: industryType } = useGetIndustryTypeListQuery();
  const { data: employmentTypeList } = useGetEmployementTypeListQuery();
  const { data: genderList } = useGetGendersListQuery();
  const productApplication = useMemo(
    () => GET_PRODUCT_APPLICATION(PRODUCT_HANDLER[product], applicationData?.data),
    [product, applicationData?.data],
  );
  const isError = errorAddInfo || consentError;
  const isloading = addInfoLoading||consentDataLoading;
  /** --------------------------ACTION-------------------------- **/
  const formik = useFormik({
    enableReinitialize: !isloading,
    validationSchema: PERSONAL_DETAILS_SCHEMA,
    initialValues: {
      companyName: productApplication?.companyName || "",
      businessName: productApplication?.businessName || "",
      industryType: productApplication?.industryType || "",
      employmentType: productApplication?.employmentType || "",
      gender: customerData?.data?.gender?.trim()?.toUpperCase() || "",
      fatherName:
        (customerData?.data?.fatherName?.trim() === "."
          ? ""
          : customerData?.data?.fatherName?.trim()) || "",
      motherName: customerData?.data?.motherName || "",
      negativeProfessionReject: "No",
    },
    onSubmit: async (values) => {
      // logWithAppId(
      //   "Submitting personal details",
      //   productApplication?.appId,
      //   {
      //     values,
      //     latitude,
      //     longitude,
      //     errors: formik.errors,
      //   },
      //   "info",
      // );

      if (latitude !== "" && longitude !== "") {
        moengage.setGender(`${customerData?.data?.gender}`);
        moengage.setFirstName(`${customerData?.data?.firstName}`);
        moengage.setLastName(`${customerData?.data?.lastName}`);
        const consent_payload = {
          customerId: customerData?.data?.id,
          appId: productApplication?.appId,
          product_type: "PL",
          type: ConsentType.CIBIL_CONSENT,
          value: {
            cibil_soft_consent: cibilConsent,
          },
        };
        try {
          await createConsent(consent_payload).unwrap();
          await addInfo({
            ...values,
            latitude,
            longitude,
            appId: productApplication?.appId,
          }).unwrap();
          AddressStoreMethods?.resetAddressStore();
        } catch (err) {
          // logWithAppId(
          //   "Personal details submit failed",
          //   productApplication?.appId,
          //   {
          //     status: err?.status,
          //     message: err?.data?.message || err?.message,
          //   },
          // );
        }
      } else {
        SnackbarComponent({
          error: true,
          errorMessage:
            "Location information is required to proceed with consent. Please enable location access.",
        });
      } 
    },
  });

  const backClick = () => {
    router.push(STAGE_BASED_ROUTING(STAGE.APPLY_LOAN));
  };

  const resetField = (fieldName) => {
    formik.setFieldValue(fieldName, formik.initialValues[fieldName], false);
    formik.setFieldTouched(fieldName, false);
    formik.setFieldError(fieldName, undefined);
  };
  const handleFieldChange = (e, regex) => {
    if (regex(e.target.value)) {
      formik.handleChange(e);
      if (e.target.name === "industryType") {
        resetField("companyName");
        resetField("businessName");
        formik.setFieldValue(
          "negativeProfessionReject",
          industryType?.find((item) => item?.emp_type_name === e.target.value)
            ?.negative_profession_reject,
        );
      }
    }
  };

  const handleFormSubmit = async () => {
    if (isloading || formik.isSubmitting) return;

    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      // logWithAppId(
      //   "Form validation errors on submit",
      //   productApplication?.appId,
      //   {
      //     errors,
      //   },
      //   "warn",
      // );
    }
    return formik.submitForm();
  };

  const industryList = industryType?.map((item) => ({
    value: item.emp_type_name,
    name: item.emp_type_name,
    id: item.emp_type_name,
    negativeProfessionReject: item.negative_profession_reject,
  }));
  /** --------------------------EFFECT-------------------------- **/
  //dispaly errors
  useErrorHandling(isError);

  // useEffect(() => {
  //   if (errorAddInfo) {
  //     logWithAppId("addCustomerInfo API failed", productApplication?.appId, {
  //       status: errorAddInfo?.status,
  //       message: errorAddInfo?.data?.message || errorAddInfo?.error,
  //     });
  //   }
  // }, [errorAddInfo]);

  useEffect(() => {
    if (navigator.geolocation && navigator.permissions) {
      navigator.permissions
        .query({ name: "geolocation" })
        .then(function (result) {
          if (result.state === "granted" || result.state === "prompt") {
            navigator.geolocation.getCurrentPosition(
              (pos) => {
                setLatitude(pos.coords.latitude.toString());
                setLongitude(pos.coords.longitude.toString());
              },
              (err) => {
                // logWithAppId(
                //   "Geolocation getCurrentPosition failed",
                //   productApplication?.appId,
                //   {
                //     code: err.code,
                //     message: err.message,
                //   },
                //   "warn",
                // );
              },
            );
          } else if (result.state === "denied") {
            // logWithAppId(
            //   "Geolocation permission denied",
            //   productApplication?.appId,
            //   {},
            //   "warn",
            // );
          }
        })
        .catch((err) => {
          // logWithAppId(
          //   "Geolocation permissions query failed",
          //   productApplication?.appId,
          //   {
          //     message: err.message,
          //   },
          // );
        });
    } else {
      // logWithAppId(
      //   "Geolocation not supported by browser",
      //   productApplication?.appId,
      //   {},
      //   "warn",
      // );
      SnackbarComponent({
        error: true,
        errorMessage: "Your browser does not support Geolocation.",
      });
    }
  }, []);

  useEffect(() => {
    if (productApplication?.stage === STAGE.PERSONAL_DETAILS) {
      sendEventAnalytics({
        eventName: WEB_EVENTS.PERSONAL_DETAILS_STARTED,
        ...productApplication,
        ...customerData?.data,
      });
    }
    if (productApplication?.stage) {
      router.push(STAGE_BASED_ROUTING(productApplication?.stage));
    }
  }, [productApplication?.stage]);

  useStageErrorHandler({
    stage: productApplication?.stage,
    prevStage: productApplication?.previousStage,
    message: productApplication?.loanReapplyMessage,
  });
  useEffect(() => {
    if (productApplication?.stage === STAGE.PERSONAL_DETAILS) {
      setActions({
        onReject:
          productApplication?.ckycOtpVerified || customerData?.data?.isPartner
            ? backClick
            : () => setShowPanEditModal(true),
        rejectBtnLabel: "Back",
        showReject: true,
        showConfirm: true,
        // handler is dropped once submitted so a late click can't re-fire it
        onConfirm: updatedCustomerInfo ? undefined : handleFormSubmit,
        confirmBtnLabel: "Proceed",
        disabledConfirmBtn:
          !cibilConsent ||
          !formik.isValid ||
          isloading ||
          formik.isSubmitting ||
          updatedCustomerInfo,
      });
    }
  }, [
    setActions,
    productApplication,
    formik.isValid,
    formik.isSubmitting,
    cibilConsent,
    isloading,
    updatedCustomerInfo,
  ]);

  const fatherNameEditable = customerData?.data?.fatherName
    ? customerData.data.fatherName === "."
      ? true // editable when value is "."
      : !productApplication?.ckycOtpVerified
        ? true // editable when in personal details stage
        : false // not editable when it has any other value
    : true;

  /** --------------------------RENDER-------------------------- **/
  return (
    <FormikProvider value={formik}>
      <FocusErrorField />
      <PaperComponent
        elevation={0}
        paperStyle={{ backgroundColor: "#E5F1F1", px: { xs: 1, md: 2 } }}
      >
        <PaperComponent
          elevation={0}
          sx={{
            backgroundColor: WEB_COLORS.lightSkyBlue,
            borderRadius: 2,
            border: "1px solid #0041354D",
          }}
        >
          <PaperComponent
            elevation={0}
            sx={{
              backgroundColor: WEB_COLORS.white,
              borderRadius: 2,
              p: 2,
            }}
          >
            <Typography mb={2}>Other Details</Typography>
            <LoaderComponent open={isloading} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={6} lg={4}>
                <TextFieldComponent
                  allowCapitalization={true}
                  required
                  maxLength={MAX_LIMIT_FOR_NAME}
                  countRequired
                  // disabled={customerData?.data?.fatherName && customerData?.data?.fatherName?.trim() !== "."}
                  disabled={!fatherNameEditable}
                  name={"fatherName"}
                  label={t("fathername")}
                  value={formik.values.fatherName}
                  // error={Boolean(
                  //   formik?.touched?.fatherName && formik?.errors?.fatherName,
                  // )}
                  // errorMessage={formik.errors.fatherName}
                  error={Boolean(
                    formik.touched.fatherName && formik.errors.fatherName,
                  )}
                  errorMessage={
                    formik.touched.fatherName && formik.errors.fatherName
                      ? formik.errors.fatherName
                      : ""
                  }
                  onBlur={formik.handleBlur}
                  onChange={(e) => {
                    formik.setFieldTouched("fatherName", true, false);
                    handleFieldChange(e, NAME_REGEX);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={4}>
                <TextFieldComponent
                  required
                  countRequired
                  allowCapitalization={true}
                  maxLength={MAX_LIMIT_FOR_NAME}
                  disabled={customerData?.data?.motherName}
                  name={"motherName"}
                  label={t("mothername")}
                  value={formik.values.motherName}
                  error={Boolean(
                    formik.touched.motherName && formik?.errors?.motherName,
                  )}
                  errorMessage={formik.errors.motherName}
                  onBlur={formik.handleBlur}
                  onChange={(e) => {
                    handleFieldChange(e, NAME_REGEX);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={4}>
                {customerData?.data?.gender &&
                !customerData?.data?.isPartner ? (
                  <TextFieldComponent
                    allowCapitalization={true}
                    required
                    inputPropsStyle={{ textTransform: "capitalize" }}
                    disabled
                    name={"gender"}
                    label={t("gender")}
                    onBlur={formik.handleBlur}
                    value={formik.values.gender}
                    errorMessage={formik.errors.gender}
                    error={Boolean(
                      formik?.touched?.gender && formik?.errors?.gender,
                    )}
                    onChange={(e) => {
                      handleFieldChange(e, NAME_REGEX);
                    }}
                  />
                ) : (
                  <SelectComponent
                    required
                    name={"gender"}
                    label={t("gender")}
                    value={formik.values.gender}
                    singleItemArray={genderList}
                    error={Boolean(
                      formik?.touched?.gender && formik?.errors?.gender,
                    )}
                    errorMessage={formik.errors.gender}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                )}
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={4}>
                <SelectComponent
                  singleItemArray={employmentTypeList}
                  required
                  value={formik.values.employmentType}
                  name={"employmentType"}
                  onChange={(e) => {
                    resetField("businessName");
                    resetField("companyName");
                    resetField("industryType");
                    formik.handleChange(e);
                  }}
                  label={"Employment Type"}
                  onBlur={formik.handleBlur}
                  error={Boolean(
                    formik?.touched?.employmentType &&
                    formik?.errors?.employmentType,
                  )}
                  errorMessage={formik.errors.employmentType}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={4}>
                <SelectComponent
                  name={"industryType"}
                  label={t("industrytype")}
                  value={formik.values.industryType}
                  menuItems={industryList}
                  error={Boolean(
                    formik?.touched?.industryType &&
                    formik?.errors?.industryType,
                  )}
                  errorMessage={formik.errors.industryType}
                  onChange={(e) => {
                    handleFieldChange(e, FREE_TEXT_REGEX);
                  }}
                  onBlur={formik.handleBlur}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={4}>
                {formik.values.employmentType === "Self Employed" && (
                  <TextFieldComponent
                    allowCapitalization={true}
                    required
                    maxLength={80}
                    name={"businessName"}
                    label={t("businessname")}
                    onBlur={formik.handleBlur}
                    placeholder="Enter business name"
                    disabled={customerData?.data?.businessName}
                    value={formik?.values?.businessName}
                    onChange={(e) => {
                      handleFieldChange(e, COMPANY_NAME_REGEX);
                    }}
                    error={Boolean(
                      formik?.touched?.businessName &&
                      formik?.errors?.businessName,
                    )}
                    errorMessage={formik?.errors?.businessName}
                  />
                )}
                {formik.values.employmentType === "Salaried" && (
                  <AutoCompleteSearch formik={formik} />
                )}
              </Grid>
            </Grid>
            <Stack mt={1}>
              <CheckboxComponent
                name="cibilConsent"
                onChange={(event) => setCibilConsent(event.target.checked)}
                checked={cibilConsent}
                label={
                  <Typography
                    fontSize={{ xs: "0.8rem", md: "1rem" }}
                    color={WEB_COLORS.disabledColor}
                  >
                    I agree to the{" "}
                    <a
                      href="https://docs.google.com/viewer?url=https://herofinmicrosrvuatpublic.s3.ap-south-1.amazonaws.com/HIPL/consent-credit-report/cibil-consent-credit-report-feature.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: WEB_COLORS.darkTeal, fontWeight: "bold" }}
                    >
                      Terms and Conditions
                    </a>{" "}
                    of TU CIBIL and hereby authorize Hero FinCorp Limited (HFCL)
                    to obtain my Consumer Credit Information from TransUnion
                    CIBIL Limited for the purpose of underwriting.
                  </Typography>
                }
              />
            </Stack>
          </PaperComponent>
        </PaperComponent>
      </PaperComponent>
    </FormikProvider>
  );
}

export default PersonalDetailsFields;
